/**
 * Polling sub-hook for CSV import analysis status.
 *
 * Encapsulates the recursive-setTimeout polling loop, poll epoch tracking,
 * and isPolling state. Extracted from useCsvImport to keep polling logic
 * isolated and independently testable.
 */

import { useCallback, useEffect, useState } from 'react';

import type { CsvImport } from '@/lib/csv-import/api';
import { getCsvImport } from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

const POLLING_INTERVAL_MS = 2000;

export interface UseImportPollingReturn {
  isPolling: boolean;
  /** Bump the epoch to (re-)start polling. Call after submit or on resume. */
  startPolling: () => void;
  /** Reset epoch to 0 to stop polling on next cleanup. */
  resetPolling: () => void;
}

/**
 * Polls the backend for import analysis status using recursive setTimeout.
 *
 * Polling starts when `startPolling()` bumps the epoch (> 0) and an importId
 * is present. It stops automatically when the backend returns a terminal
 * status (anything other than 'analyzing'). The epoch — not `status` — drives
 * the effect to avoid a feedback loop.
 *
 * @param orgId - Organization ID for API calls
 * @param importId - Current import ID (null when no import exists yet)
 * @param onData - Called with fresh data on each successful poll
 * @param onError - Called when a poll request fails
 */
export function useImportPolling(
  orgId: number,
  importId: number | null,
  onData: (data: CsvImport) => void,
  onError: (message: string) => void
): UseImportPollingReturn {
  const [isPolling, setIsPolling] = useState(false);
  const [pollEpoch, setPollEpoch] = useState(0);

  const startPolling = useCallback(() => {
    setPollEpoch(prev => prev + 1);
  }, []);

  const resetPolling = useCallback(() => {
    setPollEpoch(0);
  }, []);

  useEffect(() => {
    // Only poll when explicitly triggered (epoch > 0) and we have an import
    if (pollEpoch === 0 || !importId) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    let cancelled = false;

    const poll = async () => {
      if (cancelled) {
        return;
      }
      try {
        const data = await getCsvImport(orgId, importId);
        if (cancelled) {
          return;
        }
        onData(data);

        if (data.status === 'analyzing') {
          // Still processing — schedule next poll
          setTimeout(poll, POLLING_INTERVAL_MS);
        } else {
          // Terminal status reached — stop polling
          setIsPolling(false);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        logger.error(
          'Failed to fetch import status',
          err instanceof Error ? err : new Error(String(err)),
          { import_id: importId, org_id: orgId }
        );
        onError(
          'Unable to load import analysis. Please refresh and try again.'
        );
        setIsPolling(false);
      }
    };

    // Start first poll immediately
    poll();

    return () => {
      cancelled = true;
    };
  }, [importId, orgId, pollEpoch, onData, onError]);

  return { isPolling, startPolling, resetPolling };
}
